import { useEffect, useState } from 'react';
import { YouInvest } from 'src/Api';

export const SOLUTION_KEY = 'de5ugjg7oy';
export const CHART_TITLE = 'Name';
export const CHART_TYPE = 'mountainChart';
export const MAIN_COLOUR = '#1d74c6';
export const COMPARE_COLOURS = ['#9c27b0', '#548A2E', '#19227D', '#2095F2', '#AEB32A', '#4D332D', '#B967C7'];

export const DATA_TYPE_PRICE = 'price';
export const DATA_TYPE_PRICEANDNAV = 'priceAndNav';
export const DATA_TYPE_GROWTH10K = 'growth10K';

export const INTERVALTYPE_ONEDAY = 'OneDay';
export const INTERVALTYPE_FIVEDAY = 'FiveDay';
export const INTERVALTYPE_FIFTEENDAY = 'FifteenDay';
export const INTERVALTYPE_ONEMONTH = 'OneMonth';
export const INTERVALTYPE_THREEMONTH = 'ThreeMonth';

export const CURRENCY = 'GBP';

export const DATA_SOURCE = 'EU';
export const UNIVERSE_IDS = 'FXALL$$ALL_1392|FOGBR$$ALL_2518|CEEXG$XLON|E0EXG$XMIL|E0EXG$XMCE|E0EXG$XNAS|E0EXG$XSTO|E0EXG$XNYS|E0EXG$XAMS|E0EXG$XPAR|E0EXG$XASE|E0EXG$XSWX|E0EXG$XTSE|E0EXG$XETR|E0EXG$XLON|E0EXG$XCSE|E0EXG$XHEL|E0EXG$XICE|E0EXG$XRIS|E0EXG$XLIT|ETFEXG$XLON|CAEUR$$ALL|CAGBR$$ALL|CAGBR$$ABI|IXMSX$$ALL';

export default function Chart(props) {

    let initialDataType = DATA_TYPE_PRICE;
    if (props.instrumentType === 'FUND') initialDataType = DATA_TYPE_GROWTH10K;
    else if (props.instrumentType === 'CEF') initialDataType = DATA_TYPE_PRICEANDNAV;

    const [token, setToken] = useState('');
    const [chartDataType, setChartDataType] = useState(initialDataType);
    const [chartSettings, setChartSettings] = useState(null);

    const setOAuthToken = () => {
        // request oauth token from morningstar
        fetch(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/morningstar-oauth-token`, {
            method: 'GET',
            headers: YouInvest.getTradingRequestHeaders()
        }).then(async (response) => {
            const responseBody = await response.json();
            // store the token in local component state
            setToken(responseBody.apiTokens.apiGatewayToken);
        });
    }

    const isMorningstarReady = (loadChart) => { 
        if (window.morningstar) {
            loadChart();
        } else {
            setTimeout(() => {
                isMorningstarReady(loadChart);
            }, 250);
        }
    }

    useEffect(() => {
        if (props.chartDataType === undefined || props.chartDataType === '') { // guard for initial render
            return;
        }
        if (props.instrumentType === 'CEF') {
            window.morningstar.componentInstance.destroy();
            setChartDataType(props.chartDataType);
        }
    }, [props.chartDataType]);

    useEffect(() => {

        // check for morningstar oauth token
        if (!token) {
            // request the token and wait
            setOAuthToken();
            return;
        }

        // establish which morningstar env we should use
        const environment = window.location.hostname.indexOf('ajbbuild.uk') < 0 ? 'prod' : 'qa';

        // choose settings object based on data type
        let settings = chartDataType === DATA_TYPE_PRICEANDNAV ? { ...props.priceAndNavSettings } : { ...props.defaultSettings };
        // merge any settings that have changed since the chart was loaded
        if (chartSettings) settings = { ...settings, ...chartSettings };
        // force the the data type to whatever it got set to
        settings.charts.mainChart.dataType = chartDataType;
        settings.lang = 'en-GB';

        const isDayRange = settings.intervalType === INTERVALTYPE_ONEDAY || settings.intervalType === INTERVALTYPE_FIVEDAY || settings.intervalType === INTERVALTYPE_FIFTEENDAY;
        if (chartDataType === 'priceAndNav' && isDayRange) {
            settings.intervalType = INTERVALTYPE_ONEMONTH;
        }

        // initialise chart
        // https://developer.morningstar.com/components/market-monitoring/advanced-chart/overview
        // https://ajbell.atlassian.net/wiki/spaces/developers/pages/2319942513

        const loaderConfig = {
            lazy: {
                initializeMarketsComponent: true,
            },
            apiTokens: {
                apiGatewayToken: token,
            },
            configuration: {
                environment: environment,
                namespace: 'AJBell.OPP396510.marketsChart',
                source: {
                    type: 'none',
                },
                overrides: {
                    components: {
                        marketsSvgchart: {
                            type: 'markets-components-svgchart',
                            version: '2.92.0',
                            settings: settings
                        },
                    },
                },
            },
            component: {
                source: {
                    instid: 'AJBE'
                },
            }
        };

        isMorningstarReady(() => {
            morningstar.loader.load(loaderConfig).then(function () {
                morningstar.loader.lazyLoad(
                    document.getElementById('chart-container-parent'),
                    'chart-container',
                    'markets-components-svgchart',
                    'marketsSvgchart'
                ).then(function (componentInstance) {
                    // add component instance to window
                    window.morningstar.componentInstance = componentInstance;
                    // add event handlers
                    componentInstance.on('click', chartClick);
                    componentInstance.on('configChange', chartConfigChange);
                }).catch(function (error) {
                    console.log('error loading chart');
                    console.log(error);
                });
            });
        });

    }, [token, chartDataType]);

    const chartClick = (e) => {
        // currently only interested in changes to interval type for investment trusts
        if (e.name !== 'dateRange' || props.instrumentType !== 'CEF') {
            return;
        }
        const isDayRange = e.value === INTERVALTYPE_ONEDAY || e.value === INTERVALTYPE_FIVEDAY || e.value === INTERVALTYPE_FIFTEENDAY;
        if (chartDataType === DATA_TYPE_PRICEANDNAV && isDayRange) {
            props.updateChartDataType(DATA_TYPE_PRICE);
        }
    }

    const chartConfigChange = (config) => {
        // stash the config in local state so we can apply to a fresh chart
        setChartSettings(config.saveConfig);
    }

    return <div id="chart-container-parent" style={{ minHeight: '400px' }}></div>
}
